import { useSyncExternalStore } from 'react'

/**
 * THE DEVICE SEAM.
 *
 * Device class is not a screen size — it is a different gaming experience, and
 * therefore a different interaction model. A surface may branch on it, and when
 * it does the branch is explicit and visible here rather than scattered through
 * breakpoints in six stylesheets.
 *
 * Named, not built: per the rule that governs Format
 * (docs/guides/packs/inhabit-format.md §"a named seam, not a built abstraction"),
 * this exists so a fork is a deliberate decision with somewhere to live. The
 * second surface gets written when a real pack demands it — not before.
 *
 * CLASSIFIED BY POINTER, NOT WIDTH, because what actually differs is input:
 *
 *   mobile   coarse pointer, narrow  — a thumb; composing prose is painful
 *   tablet   coarse pointer, wide    — a thumb, but with room
 *   desktop  fine pointer            — keyboard and mouse; laptop and
 *                                      stationary are the same experience
 *
 * Width alone gets the two interesting cases wrong: a narrow desktop window is
 * still a desktop (you have a keyboard), and a tablet is wide but you are still
 * tapping.
 */
export type DeviceClass = 'mobile' | 'tablet' | 'desktop'

/** Above this, a touch device has room for more than a single column. */
const TABLET_MIN_WIDTH = 820

const QUERIES = ['(pointer: coarse)', `(min-width: ${TABLET_MIN_WIDTH}px)`]

export function resolveDeviceClass(): DeviceClass {
  // SSR/no-matchMedia safety: assume the roomier case rather than crippling it.
  if (typeof window === 'undefined' || !window.matchMedia) return 'desktop'

  const coarse = window.matchMedia('(pointer: coarse)').matches
  if (!coarse) return 'desktop'

  return window.matchMedia(`(min-width: ${TABLET_MIN_WIDTH}px)`).matches
    ? 'tablet'
    : 'mobile'
}

/**
 * Re-evaluates on rotation or a resize that crosses the boundary. Uses
 * useSyncExternalStore so the value is read consistently and never tears
 * between a render and an effect.
 */
export function useDeviceClass(): DeviceClass {
  return useSyncExternalStore(subscribe, resolveDeviceClass, () => 'desktop')
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}

  const lists = QUERIES.map((q) => window.matchMedia(q))
  for (const l of lists) l.addEventListener('change', onChange)
  return () => {
    for (const l of lists) l.removeEventListener('change', onChange)
  }
}
